import React, { useState, useEffect, useRef, memo } from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../components/ui/tooltip";
import {
  ImageIcon as Image,
  FileVideo,
  X,
  Maximize2,
  Clock,
  MessageCircle,
} from "lucide-react";

// Update the type checking helpers at the top
const isImageType = (type) => {
  const imageTypes = ["jpg", "jpeg", "png", "gif"];
  return imageTypes.includes(type?.toLowerCase());
};

const isVideoType = (type) => {
  const videoTypes = ["mp4", "mov", "avi", "webm", "quicktime"];
  return videoTypes.includes(type?.toLowerCase());
};

// Media Item Preview Component
const MediaPreviewTooltip = memo(function MediaPreviewTooltip({
  type,
  fileName,
  title,
  mediaSrc,
  duration: propDuration = null,
  children,
}) {
  const [isLoading, setIsLoading] = useState(true);
  const [duration, setDuration] = useState(propDuration);
  const [error, setError] = useState(false);
  const videoRef = useRef(null);
  const imageRef = useRef(null);

  // Format time in seconds to MM:SS format
  const formatTime = (timeInSeconds) => {
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  // Update the useEffect for media loading
  useEffect(() => {
    setIsLoading(true);
    setError(false);

    if (isImageType(type)) {
      const img = new window.Image();
      img.src = mediaSrc;

      img.onload = () => {
        setIsLoading(false);
      };

      img.onerror = () => {
        setIsLoading(false);
        setError(true);
      };

      return () => {
        img.onload = null;
        img.onerror = null;
      };
    }
  }, [type, mediaSrc]);

  // Separate useEffect for video handling
  useEffect(() => {
    if (!isVideoType(type)) return;

    const video = videoRef.current;
    if (!video) return;

    const handleLoadedMetadata = () => {
      if (video.duration && video.duration !== Infinity) {
        setDuration(formatTime(video.duration));
      }
      setIsLoading(false);
    };

    const handleError = () => {
      setIsLoading(false);
      setError(true);
    };

    video.addEventListener("loadedmetadata", handleLoadedMetadata);
    video.addEventListener("error", handleError);

    // Try to load the video
    video.load();

    return () => {
      video.removeEventListener("loadedmetadata", handleLoadedMetadata);
      video.removeEventListener("error", handleError);
    };
  }, [type, videoRef.current]); // Add videoRef.current as dependency

  // Update the renderIcon function
  const renderIcon = () => {
    if (isImageType(type)) {
      return <Image size={16} className="text-gray-600" />;
    } else if (isVideoType(type)) {
      return <FileVideo size={16} className="text-gray-600" />;
    }
    return <FileVideo size={16} className="text-gray-600" />; // default to video icon
  };

  // Update the renderPreview function
  const renderPreview = () => {
    if (isVideoType(type)) {
      return (
        <video
          ref={videoRef}
          key={mediaSrc} // Add key to force re-render when source changes
          src={mediaSrc}
          className="w-full h-full object-cover"
          muted
          playsInline
          preload="metadata"
          onError={() => setError(true)}
          controls
        />
      );
    }

    if (isLoading) {
      return (
        <div className="flex items-center justify-center w-full h-full bg-gray-100">
          <Clock className="w-6 h-6 text-gray-400 animate-pulse" />
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex items-center justify-center w-full h-full bg-gray-100">
          <span className="text-sm text-gray-500">Failed to load preview</span>
        </div>
      );
    }

    if (isImageType(type)) {
      return (
        <img
          ref={imageRef}
          src={mediaSrc}
          alt={title}
          className="w-full h-full object-cover"
          onError={() => setError(true)}
        />
      );
    }

    return (
      <div className="flex items-center justify-center w-full h-full bg-gray-100">
        <span className="text-sm text-gray-500">Unsupported file type</span>
      </div>
    );
  };

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          {children || (
            <button className="p-1 rounded-full hover:bg-gray-100 transition-colors">
              {renderIcon()}
            </button>
          )}
        </TooltipTrigger>
        <TooltipContent
          side="left"
          align="center"
          className="p-0 border-0 shadow-xl rounded-lg overflow-hidden bg-transparent w-64"
          hideWhenDetached={false}
        >
          <div className="relative bg-white rounded-lg shadow-xl overflow-hidden">
            {/* Preview Container */}
            <div className="relative w-full h-48 bg-gray-100">
              {renderPreview()}

              {/* Video Duration Badge */}
              {!isImageType(type) && duration && !isLoading && !error && (
                <div className="absolute bottom-2 right-2 bg-black bg-opacity-70 text-white px-2 py-1 rounded text-xs">
                  {duration}
                </div>
              )}
            </div>

            {/* File Info */}
            <div className="p-3 bg-white">
              <div className="font-medium text-sm text-gray-800 truncate">
                {title || fileName}
              </div>
              <div className="flex items-center justify-between mt-2">
                <div className="text-xs text-gray-500 truncate max-w-[70%]">
                  {fileName}
                </div>
                <div className="flex space-x-2 items-center">
                  <div className="flex items-center justify-center px-2 py-1 bg-gray-100 rounded text-xs text-gray-600 font-medium">
                    {isImageType(type)
                      ? "Image"
                      : isVideoType(type)
                      ? "Video"
                      : "File"}
                  </div>
                  <button className="text-gray-400 hover:text-gray-600">
                    <MessageCircle size={14} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
});

export { MediaPreviewTooltip };
